import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Eye, Cookie, Database, Mail, Clock } from 'lucide-react';
import SeoHead from '@/components/shared/SeoHead';
import { BRAND_OPERATOR_LINE, COMPANY, formatCompanyMailingAddress } from '@/lib/companyInfo';


const Privacy = () => {
  return (
    <>
      <SeoHead
        title="Privacy Policy - Wage Atlas | Data Protection & Privacy Rights"
        description="Wage Atlas privacy policy. Learn how we collect, use, and protect your personal information when using our salary research tools and career insights."
        canonical="https://wageatlas.com/privacy/"
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="h-8 w-8 text-primary" />
                <h1 className="text-3xl font-bold">Privacy Policy</h1>
              </div>
              <p className="text-lg text-muted-foreground">
                Your privacy matters to us. This policy explains how {COMPANY.brandName} ({COMPANY.legalName}) collects, uses, and protects your information.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Last updated: June 2026</span>
              </div>
              <p className="text-sm text-muted-foreground">{BRAND_OPERATOR_LINE}</p>
            </div>

            {/* Information Collection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Personal Information</h3>
                  <p className="text-muted-foreground mb-2">
                    When you create an account or use our services, we may collect:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>Email address and display name</li>
                    <li>Payment information for optional PDF purchases</li>
                    <li>Profile preferences and settings</li>
                    <li>Search queries and usage patterns</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                    <li>IP address and location data</li>
                    <li>Browser type and device information</li>
                    <li>Page views and interaction data</li>
                    <li>Referral sources and navigation patterns</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-primary" />
                  How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">
                      <strong>Service Delivery:</strong> To provide salary data, career insights, and personalized recommendations
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">
                      <strong>Account Management:</strong> To authenticate users and manage account preferences
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">
                      <strong>Analytics:</strong> To understand usage patterns and improve our services
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">
                      <strong>Communication:</strong> To send service updates, security alerts, and marketing (with consent)
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Cookies & Tracking */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cookie className="h-5 w-5 text-primary" />
                  Cookies & Tracking Technologies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  We use cookies and similar technologies to enhance your experience:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Essential Cookies</h4>
                    <p className="text-sm text-muted-foreground">
                      Required for basic site functionality, authentication, and security.
                    </p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Analytics Cookies</h4>
                    <p className="text-sm text-muted-foreground">
                      Help us understand how users interact with our site to improve services.
                    </p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Advertising Cookies</h4>
                    <p className="text-sm text-muted-foreground">
                      Used to display relevant ads and measure advertising effectiveness.
                    </p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Preference Cookies</h4>
                    <p className="text-sm text-muted-foreground">
                      Remember your settings and preferences for a personalized experience.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Protection */}
            <Card>
              <CardHeader>
                <CardTitle>Data Protection & Security</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-muted-foreground">SSL/TLS encryption for data transmission</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-muted-foreground">Secure database storage with encryption at rest</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-muted-foreground">Regular security audits and vulnerability assessments</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-muted-foreground">Access controls and employee training</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card>
              <CardHeader>
                <CardTitle>Your Privacy Rights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Depending on your location, you may have the following rights:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Access & Portability</h4>
                    <p className="text-sm text-muted-foreground">
                      Request access to your personal data and receive it in a portable format.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Correction & Updates</h4>
                    <p className="text-sm text-muted-foreground">
                      Update or correct inaccurate personal information.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Deletion</h4>
                    <p className="text-sm text-muted-foreground">
                      Request deletion of your personal data under certain circumstances.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Opt-Out</h4>
                    <p className="text-sm text-muted-foreground">
                      Withdraw consent for marketing communications and data processing.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Paid Partnerships */}
            <Card>
              <CardHeader>
                <CardTitle>Paid Partnerships &amp; Sponsored Placements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  {COMPANY.brandName} may display sponsored program placements from workforce development and training partners.
                  These are paid advertisements, clearly labeled on the site. Sponsor modules may use cookies or tracking parameters
                  on outbound links consistent with our advertising and affiliate practices.
                </p>
                <p className="text-muted-foreground">
                  We do not sell your personal information to sponsors. Sponsorship reporting uses aggregate impression and click metrics only.
                </p>
              </CardContent>
            </Card>

            {/* Affiliate Relationships */}
            <Card>
              <CardHeader>
                <CardTitle>Affiliate Relationships & Disclosure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    Wage Atlas participates in various affiliate marketing programs. This means that when you click 
                    on certain links on our website and make a purchase or sign up for a service, we may receive a 
                    commission at no additional cost to you.
                  </p>
                  <div>
                    <h3 className="font-semibold mb-2">Affiliate Partners</h3>
                    <p className="text-muted-foreground mb-2">
                      We maintain affiliate relationships with:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                      <li>Job boards and career platforms (ZipRecruiter, Indeed, LinkedIn, Monster, etc.)</li>
                      <li>Education and training platforms (Coursera, Udemy, Skillshare, edX, etc.)</li>
                      <li>Resume services (TopResume, Resume.io, Zety, etc.)</li>
                      <li>Career resources and professional development tools</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Transparency & Objectivity</h3>
                    <p className="text-muted-foreground">
                      Our affiliate relationships help us keep Wage Atlas free and accessible. We only recommend 
                      products and services we believe will be valuable to our users. All opinions, reviews, and 
                      recommendations are our own and are not influenced by affiliate commissions. We clearly 
                      disclose affiliate relationships where applicable.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Tracking & Cookies</h3>
                    <p className="text-muted-foreground">
                      When you click on affiliate links, our partners may place cookies on your device to track 
                      referrals. These cookies help us receive credit for referrals and do not collect personal 
                      information beyond what is necessary for the affiliate relationship. You can manage cookie 
                      preferences through your browser settings.
                    </p>
                  </div>
                </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  If you have questions about this privacy policy or want to exercise your privacy rights, contact us:
                </p>
                <div className="space-y-2">
                  <p><strong>Data controller:</strong> {COMPANY.legalName}</p>
                  <p><strong>Email:</strong>{' '}
                    <a href={`mailto:${COMPANY.emails.privacy}`} className="text-primary hover:underline">
                      {COMPANY.emails.privacy}
                    </a>
                  </p>
                  <p><strong>Response Time:</strong> We respond to privacy requests within 30 days</p>
                  <p><strong>Address:</strong> {formatCompanyMailingAddress()}</p>
                </div>
              </CardContent>
            </Card>

            {/* Updates */}
            <Card>
              <CardHeader>
                <CardTitle>Policy Updates</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We may update this privacy policy periodically to reflect changes in our practices or legal requirements. 
                  We will notify you of material changes via email or prominent notice on our website. 
                  Your continued use of our services after policy updates constitutes acceptance of the revised terms.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;